import { AlertTriangle, ClipboardCheck, Download, ExternalLink, FileDown, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import { Modal } from "../components/Modal";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { disputeStatusIcon } from "../lib/icons";
import {
  disputes as disputesApi,
  letters as lettersApi,
  mail as mailApi,
  request,
  type DisputeRound,
  type Letter,
  type MailProviderId,
} from "../lib/api";

// Letter kinds that bundle into a per-bureau PDF. §623 furnisher letters and
// the LexisNexis letter go to a different recipient and stay separate — they
// get a per-letter "Download PDF" button instead.
const BUREAU_BUNDLE_KINDS = new Set([
  "letter_605b",
  "late_payment",
  "public_records_dispute",
  "manual",
]);

// Friendly labels for the letter-kind enum — what staff sees in the round
// detail breakdown. These are the precise UI labels Key approved for the
// new routing rules (§605B Block — Collections etc.). When a letter row
// carries a quality_gate_notes.section_label, that takes precedence over
// the kind default so each §605B sub-letter is correctly tagged.
const LETTER_KIND_LABEL: Record<string, string> = {
  letter_605b: "§605B Block — Hard Inquiries",
  late_payment: "Late Payment Dispute",
  public_records_dispute: "Public Records",
  lexisnexis_dispute: "LexisNexis Risk Solutions",
  section_623_dispute: "§623 Furnisher Dispute",
  manual: "§611 Account Reinvestigation",
};

// Pull the analyzer's section_label / manual_section_label discriminator out
// of quality_gate_notes. quality_gate_notes is a JSON-encoded string the
// generator writes; section_label is the new precise tag (e.g. "§605B
// Block — Collections"), and manual_section_label is the legacy field for
// §611 sub-sections. Prefer section_label when present.
function letterDisplayKind(l: Letter): string {
  const raw = l.quality_gate_notes;
  if (typeof raw === "string" && raw.startsWith("{")) {
    try {
      const parsed = JSON.parse(raw) as {
        section_label?: string;
        manual_section_label?: string;
      };
      if (parsed.section_label) return parsed.section_label;
      if (parsed.manual_section_label) return parsed.manual_section_label;
    } catch {
      /* ignore — fall through to enum label */
    }
  }
  return LETTER_KIND_LABEL[l.kind] || l.kind;
}

// Pull furnisher name (for §623 letters) out of quality_gate_notes when set.
function letterFurnisherName(l: Letter): string | null {
  const raw = l.quality_gate_notes;
  if (typeof raw === "string" && raw.startsWith("{")) {
    try {
      const parsed = JSON.parse(raw) as { furnisher_name?: string };
      if (parsed.furnisher_name) return parsed.furnisher_name;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function letterBureauKey(l: Letter): string {
  return (l.bureau || (l as any).recipient_bureau || "").toLowerCase();
}

// Trigger an authenticated PDF download. The /api/uploads-style endpoints
// require Bearer auth so we can't just use an <a href> — fetch the bytes,
// blob() them, and synthesize a download click.
async function downloadPdf(path: string, fallbackFilename: string) {
  const token =
    typeof window !== "undefined" ? localStorage.getItem("nedm.staff.token") : null;
  const res = await fetch(path, {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let detail = "";
    try {
      detail = (await res.json())?.message || "";
    } catch {
      /* ignore */
    }
    throw new Error(`Download failed (${res.status}) ${detail}`.trim());
  }
  const blob = await res.blob();
  // Pull filename from Content-Disposition when present.
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  const filename = m?.[1] || fallbackFilename;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function DisputesPage() {
  const [rows, setRows] = useState<DisputeRound[]>([]);
  const [status, setStatus] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<DisputeRound | null>(null);
  const [openLetters, setOpenLetters] = useState<Letter[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);
  // Bundle download state — generation renders every letter to PDF server-side
  // (slow, ~30-60s cold) and the renderer only tolerates one at a time. Track
  // the in-flight bureau so the buttons show progress instead of going silent,
  // and surface failures right next to the buttons where the user is looking.
  const [bundleBusy, setBundleBusy] = useState<"experian" | "transunion" | "equifax" | null>(null);
  const [bundleErr, setBundleErr] = useState<string | null>(null);
  // Carrier for the "Queue for Mail Center" action. Intentionally starts
  // empty — no provider is the house default; the operator picks one.
  const [queueProvider, setQueueProvider] = useState<"" | MailProviderId>("");
  // Lob submission modal state — staff must click + confirm to mail.
  type LobBureau = "experian" | "transunion" | "equifax";
  type LobMailType = "usps_first_class" | "certified";
  const [lobBureau, setLobBureau] = useState<LobBureau | null>(null);
  const [lobMailType, setLobMailType] = useState<LobMailType>("usps_first_class");
  const [lobSubmitting, setLobSubmitting] = useState(false);
  const [lobErr, setLobErr] = useState<string | null>(null);
  const [lobResult, setLobResult] = useState<{
    lob_id: string;
    tracking_url: string | null;
    expected_delivery: string | null;
    mail_job_id: string;
  } | null>(null);
  const nav = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const items = await disputesApi.list({ status: status === "all" ? undefined : status });
      setRows(items);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  async function openRound(r: DisputeRound) {
    setOpen(r);
    setOpenLetters([]);
    setDetailErr(null);
    setDetailLoading(true);
    try {
      // Refresh the round so we have the freshest items_count + status.
      const fresh = await disputesApi.get(r.id).catch(() => r);
      setOpen(fresh);
      // The backend list endpoint supports round_id; the typed wrapper now
      // forwards it. This avoids needing to fetch the whole client's letters
      // and filter client-side.
      const ls = await lettersApi.list({ round_id: r.id });
      setOpenLetters(ls);
    } catch (e: any) {
      setDetailErr(e?.message || "Could not load round detail");
    } finally {
      setDetailLoading(false);
    }
  }

  // Open the per-bureau Lob submission modal. The modal asks the staff member
  // to pick first-class vs certified and confirm before any request leaves
  // the browser. NEVER fires automatically.
  function openLobModal(bureau: LobBureau) {
    setLobBureau(bureau);
    setLobMailType("usps_first_class");
    setLobErr(null);
    setLobResult(null);
  }

  async function submitLobMail() {
    if (!open || !lobBureau) return;
    setLobSubmitting(true);
    setLobErr(null);
    setLobResult(null);
    try {
      const r = await request<{
        ok: true;
        mail_job_id: string;
        lob_id: string;
        expected_delivery: string | null;
        tracking_url: string | null;
      }>(`/api/dispute-rounds/${open.id}/mail-via-lob`, {
        method: "POST",
        body: {
          bureau: lobBureau,
          mail_type: lobMailType,
          confirm: true,
        },
      });
      setLobResult({
        lob_id: r.lob_id,
        tracking_url: r.tracking_url,
        expected_delivery: r.expected_delivery,
        mail_job_id: r.mail_job_id,
      });
    } catch (e: any) {
      // Surface 503 lob_not_configured as a calm hint instead of an error blob.
      const code = e?.body?.error || "";
      const msg = e?.body?.message || e?.message || "Lob submission failed";
      if (code === "lob_not_configured") {
        setLobErr("Lob not configured yet — add LOB_API_KEY to Worker secrets.");
      } else {
        setLobErr(msg);
      }
    } finally {
      setLobSubmitting(false);
    }
  }

  async function advance(round: DisputeRound, nextStatus: DisputeRound["status"]) {
    setAdvancing(true);
    try {
      // disputesApi.advance hits PATCH /api/dispute-rounds/:id which enforces
      // the linear status ladder server-side.
      let extra: { confirm: boolean; letters: { id: string; revision: string }[] } | undefined;
      if (nextStatus === "mailed") {
        // Record exactly the letters on screen (at the version shown) as mailed by hand.
        const picks = openLetters.filter((l: any) => !["mailed", "responded", "queued_for_mail"].includes(l.status)
          && (l.body_text || "").trim() && l.revision);
        if (!picks.length) { setDetailErr("No unsent letters with wording in this round to mark as mailed."); return; }
        if (!window.confirm(`Record ${picks.length} letter${picks.length === 1 ? "" : "s"} in this round as mailed today (mailed outside the Hub)?`)) return;
        extra = { confirm: true, letters: picks.map((l: any) => ({ id: l.id, revision: l.revision })) };
      }
      const updated = await disputesApi.advance(round.id, nextStatus, extra);
      setOpen(updated);
      await load();
    } catch (e: any) {
      setDetailErr(e?.message || "Could not advance status");
    } finally { setAdvancing(false); }
  }

  return (
    <Shell title="Disputes" subtitle="Dispute operations" actions={<span />}>
      <div className="panel">
        <div className="panel-header">
          <div>
            <h2><ClipboardCheck size={18} aria-hidden /> Rounds</h2>
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Filter rounds by status"
          >
            <option value="all">All statuses</option>
            <option value="drafting">Drafting</option>
            <option value="ready">Ready</option>
            <option value="mailed">Mailed</option>
            <option value="response_due">Response due</option>
            <option value="complete">Complete</option>
          </select>
        </div>
        {loading ? <p className="muted">Loading…</p> : rows.length === 0 ? (
          <EmptyState
            icon={<ClipboardCheck size={26} />}
            title="No dispute rounds yet"
            hint="A round is created from a client's analysis findings. Open a client, run the analyzer, then start a dispute round to draft the letters."
          />
        ) : (
          <div className="table-list">
            <div className="table-row table-head" aria-hidden="true">
              <span />
              <span>Client</span>
              <span>Round</span>
              <span>Bureau</span>
              <span>Items</span>
              <span>Status</span>
              <span>Due</span>
            </div>
            {rows.map((r) => {
              const { icon: statusIcon, tone } = disputeStatusIcon(r.status);
              return (
              <div
                className="table-row"
                key={r.id}
                role="button"
                tabIndex={0}
                onClick={() => openRound(r)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openRound(r);
                  }
                }}
                style={{ cursor: "pointer" }}
                aria-label={`Open round ${r.round_number} for ${r.client_name || r.client_id.slice(0, 8)}`}
              >
                <span className={`row-icon ${tone}`}>{statusIcon}</span>
                <strong>{r.client_name || r.client_id.slice(0, 8)}</strong>
                <span>Round {r.round_number}</span>
                <span>{r.bureau}</span>
                <span>{r.items_count} items</span>
                <StatusPill status={r.status} />
                <span>{r.due_date || "—"}</span>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={!!open}
        title={open ? `Round ${open.round_number} · ${open.client_name || open.client_id.slice(0, 8)}` : "Round"}
        onClose={() => { setOpen(null); setOpenLetters([]); setDetailErr(null); }}
        footer={open ? (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
            {open.client_id && (
              <button
                className="ghost-button"
                onClick={() => nav(`/clients/${open.client_id}`)}
              >
                <ExternalLink size={14} aria-hidden /> Open client
              </button>
            )}
            {open.status === "drafting" && (
              <button
                className="primary-button"
                onClick={() => advance(open, "ready" as DisputeRound["status"])}
                disabled={advancing}
              >
                {advancing ? "Saving…" : "Mark Ready"}
              </button>
            )}
            {open.status === "ready" && (
              <button
                className="primary-button"
                onClick={() => advance(open, "mailed" as DisputeRound["status"])}
                disabled={advancing}
              >
                {advancing ? "Saving…" : "Mark Mailed"}
              </button>
            )}
          </div>
        ) : undefined}
      >
        {detailLoading && <p className="muted small">Loading round…</p>}
        {detailErr && <p className="error-text">{detailErr}</p>}
        {open && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="identity-grid">
              <div className="field-pair">
                <span className="field-label">Status</span>
                <StatusPill status={open.status} />
              </div>
              <div className="field-pair">
                <span className="field-label">Bureau</span>
                <span>{open.bureau || "—"}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Items</span>
                <span>{open.items_count}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Created</span>
                <span>{open.created_at ? new Date(open.created_at).toLocaleString() : "—"}</span>
              </div>
            </div>

            <div>
              <span className="field-label">Letters in this round</span>
              {openLetters.length === 0 ? (
                <p className="muted small" style={{ marginTop: 4 }}>
                  {detailLoading ? "" : "No letters drafted yet."}
                </p>
              ) : (
                <div className="table-list" style={{ marginTop: 4 }}>
                  {openLetters.map((l) => {
                    const isBundleKind = BUREAU_BUNDLE_KINDS.has(l.kind);
                    const openLetterBody = async () => {
                      try {
                        const detail = await request<{ ok: true; letter: any }>(`/api/letters/${l.id}`);
                        const w = window.open("", "_blank", "width=700,height=900");
                        if (w) {
                          const safe = (s: string) =>
                            (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                          const body = (detail as any).letter?.body_text || "(no body)";
                          w.document.write(
                            `<!doctype html><html><head><title>Letter ${l.id.slice(0,8)}</title>` +
                            `<style>body{font:14px/1.5 -apple-system,system-ui,sans-serif;padding:32px;max-width:7in;margin:0 auto;}h2{margin:0 0 16px;}pre{white-space:pre-wrap;font:14px/1.6 Georgia,serif;}</style>` +
                            `</head><body><h2>${safe(l.kind)} · ${safe(l.bureau || l.recipient || "")}</h2>` +
                            `<pre>${safe(body)}</pre></body></html>`,
                          );
                          w.document.close();
                        }
                      } catch (e: any) {
                        alert(e?.message || "Could not open letter");
                      }
                    };
                    return (
                    <div
                      className="table-row"
                      key={l.id}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={openLetterBody}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openLetterBody();
                        }
                      }}
                      aria-label={`Open letter ${letterDisplayKind(l)} for ${l.bureau || l.recipient || "recipient"}`}
                    >
                      <span>{l.id.slice(0, 8)}</span>
                      <strong title={l.kind}>{letterDisplayKind(l)}</strong>
                      <span>{l.bureau || l.recipient || "—"}</span>
                      <StatusPill status={l.status} />
                      {!isBundleKind && (
                        <button
                          className="ghost-button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await downloadPdf(
                                `/api/letters/${l.id}/download`,
                                `letter-${l.id.slice(0, 8)}.pdf`,
                              );
                            } catch (err: any) {
                              setDetailErr(err?.message || "Download failed");
                            }
                          }}
                          title="Download this letter as a single PDF (§623 / LexisNexis letters mail separately)"
                        >
                          <Download size={14} aria-hidden /> PDF
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {openLetters.some((l) => BUREAU_BUNDLE_KINDS.has(l.kind)) && (
              <div>
                <span className="field-label">Letter breakdown by bureau</span>
                <div style={{ marginTop: 6, marginBottom: 12 }}>
                  {(["experian", "transunion", "equifax"] as const).map((bureau) => {
                    const lettersForBureau = openLetters.filter(
                      (l) => BUREAU_BUNDLE_KINDS.has(l.kind) && letterBureauKey(l) === bureau,
                    );
                    const label =
                      bureau === "experian" ? "Experian" : bureau === "transunion" ? "TransUnion" : "Equifax";
                    if (lettersForBureau.length === 0) {
                      return (
                        <div key={bureau} className="small" style={{ marginBottom: 2 }}>
                          <strong>{label}</strong> <span className="muted">(0 letters)</span>
                        </div>
                      );
                    }
                    return (
                      <div key={bureau} className="small" style={{ marginBottom: 2 }}>
                        <strong>{label}</strong>{" "}
                        <span className="muted">
                          ({lettersForBureau.length} letter{lettersForBureau.length === 1 ? "" : "s"}):
                        </span>{" "}
                        {lettersForBureau.map((l, idx) => (
                          <span key={l.id}>
                            {idx > 0 && <span style={{ opacity: 0.5 }}> · </span>}
                            <span>{letterDisplayKind(l)}</span>
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>

                <span className="field-label">Per-bureau mail bundles</span>
                <p className="muted small" style={{ marginTop: 2, marginBottom: 8 }}>
                  Each bundle is one PDF: every bureau-targeted letter as separate pages, followed by the client's intake documents.
                </p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(["experian", "transunion", "equifax"] as const).map((bureau) => {
                    const count = openLetters.filter(
                      (l) =>
                        BUREAU_BUNDLE_KINDS.has(l.kind) &&
                        (l.bureau || (l as any).recipient_bureau || "").toLowerCase() === bureau,
                    ).length;
                    const label =
                      bureau === "experian" ? "Experian" : bureau === "transunion" ? "TransUnion" : "Equifax";
                    return (
                      <div
                        key={bureau}
                        style={{ display: "flex", gap: 6, alignItems: "center" }}
                      >
                        <button
                          className="primary-button"
                          disabled={count === 0 || bundleBusy !== null}
                          title={
                            count === 0
                              ? `No ${label} letters in this round`
                              : bundleBusy !== null
                                ? "A bundle is already generating — one at a time keeps the PDF renderer happy"
                                : `Download the ${label} bundle (${count} letter${count === 1 ? "" : "s"} + intake docs)`
                          }
                          onClick={async () => {
                            setBundleBusy(bureau);
                            setBundleErr(null);
                            try {
                              await downloadPdf(
                                `/api/dispute-rounds/${open.id}/bundle?bureau=${bureau}`,
                                `${bureau}-round-${open.round_number}.pdf`,
                              );
                            } catch (err: any) {
                              setBundleErr(
                                `${label} bundle failed: ${err?.message || "unknown error"} — wait a few seconds and try again.`,
                              );
                            } finally {
                              setBundleBusy(null);
                            }
                          }}
                        >
                          <FileDown size={14} aria-hidden />{" "}
                          {bundleBusy === bureau ? "Generating… (can take a minute)" : `${label} bundle`}
                          {bundleBusy !== bureau && count > 0 && (
                            <span style={{ opacity: 0.7, marginLeft: 6 }}>({count})</span>
                          )}
                        </button>
                        <button
                          className="ghost-button"
                          disabled={count === 0}
                          title={
                            count === 0
                              ? `No ${label} letters in this round`
                              : `Submit the ${label} bundle to Lob for physical mailing`
                          }
                          onClick={() => openLobModal(bureau)}
                          style={{ borderColor: "var(--ne-silver-2, #c0c0c0)" }}
                        >
                          <Mail size={14} aria-hidden /> Mail via Lob
                        </button>
                        <select
                          className="input"
                          aria-label={`Mail carrier for the ${label} queue`}
                          value={queueProvider}
                          onChange={(e) => setQueueProvider(e.target.value as MailProviderId | "")}
                          style={{ height: 32, padding: "0 6px", fontSize: 12 }}
                        >
                          <option value="">Carrier…</option>
                          <option value="lob">Lob</option>
                          <option value="letterstream">LetterStream</option>
                          <option value="manual">Print &amp; mail myself</option>
                        </select>
                        <button
                          className="ghost-button"
                          disabled={count === 0 || !queueProvider}
                          title={
                            count === 0
                              ? `No ${label} letters in this round`
                              : !queueProvider
                                ? "Pick a mail carrier first"
                                : `Queue each ${label} letter as a mail job in the Mail Center — operators batch-review before dispatch`
                          }
                          onClick={async () => {
                            const lettersForBureau = openLetters.filter(
                              (l) =>
                                BUREAU_BUNDLE_KINDS.has(l.kind) &&
                                (l.bureau || (l as any).recipient_bureau || "")
                                  .toLowerCase() === bureau,
                            );
                            if (lettersForBureau.length === 0) return;
                            try {
                              setDetailErr(null);
                              let queued = 0;
                              for (const l of lettersForBureau) {
                                await mailApi.queue(l.id, "first_class", queueProvider as MailProviderId);
                                queued += 1;
                              }
                              setDetailErr(
                                `Queued ${queued} ${label} letter${queued === 1 ? "" : "s"} for the Mail Center.`,
                              );
                            } catch (err: any) {
                              setDetailErr(
                                err?.message || `Could not queue ${label} for Mail Center`,
                              );
                            }
                          }}
                        >
                          <Mail size={14} aria-hidden /> Queue for Mail Center
                        </button>
                      </div>
                    );
                  })}
                </div>
                {bundleErr && (
                  <p className="error-text" role="alert" style={{ marginTop: 8 }}>
                    {bundleErr}
                  </p>
                )}
              </div>
            )}

            {/* Creditor letters panel — §623 letters mail directly to the
                creditor / furnisher, NOT to any bureau. These letters are
                NEVER included in any bureau bundle PDF. Each has its own
                standalone Download PDF button. */}
            {openLetters.some((l) => l.kind === "section_623_dispute") && (
              <div
                style={{
                  borderTop: "1px solid var(--ne-silver-2, #c0c0c0)",
                  paddingTop: 12,
                  marginTop: 4,
                }}
              >
                <span
                  className="field-label"
                  style={{ color: "var(--ne-red, #b3261e)", fontWeight: 700 }}
                >
                  Creditor letters — mail directly to creditor, NOT to bureaus
                </span>
                <p
                  className="small"
                  style={{
                    marginTop: 4,
                    marginBottom: 10,
                    color: "var(--ne-red-dark, #8c1d18)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <AlertTriangle size={14} aria-hidden />
                  <span>
                    Do NOT bundle these with bureau letters. Each §623 letter is
                    a standalone PDF addressed to the named creditor.
                  </span>
                </p>
                <div className="table-list">
                  {openLetters
                    .filter((l) => l.kind === "section_623_dispute")
                    .map((l) => {
                      const furnisher =
                        letterFurnisherName(l) ||
                        (l.recipient_address || "").split("\n")[0] ||
                        "Creditor";
                      return (
                        <div className="table-row" key={l.id}>
                          <span>{l.id.slice(0, 8)}</span>
                          <strong>{furnisher}</strong>
                          <span>{letterDisplayKind(l)}</span>
                          <StatusPill status={l.status} />
                          <button
                            className="ghost-button"
                            onClick={async () => {
                              try {
                                await downloadPdf(
                                  `/api/letters/${l.id}/download`,
                                  `creditor-${l.id.slice(0, 8)}.pdf`,
                                );
                              } catch (err: any) {
                                setDetailErr(err?.message || "Download failed");
                              }
                            }}
                            title="Download this §623 creditor letter as a standalone PDF"
                          >
                            <Download size={14} aria-hidden /> Download PDF
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* LexisNexis letter panel — mailed to LexisNexis Risk Solutions
                Consumer Center in Atlanta. Standalone. Never bundled. */}
            {openLetters.some((l) => l.kind === "lexisnexis_dispute") && (
              <div
                style={{
                  borderTop: "1px solid var(--ne-silver-2, #c0c0c0)",
                  paddingTop: 12,
                  marginTop: 4,
                }}
              >
                <span
                  className="field-label"
                  style={{ color: "var(--ne-red, #b3261e)", fontWeight: 700 }}
                >
                  LexisNexis letter — mail directly to LexisNexis, NOT to bureaus
                </span>
                <p
                  className="small"
                  style={{
                    marginTop: 4,
                    marginBottom: 10,
                    color: "var(--ne-red-dark, #8c1d18)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <AlertTriangle size={14} aria-hidden />
                  <span>
                    Do NOT bundle with bureau letters. Source-direct dispute to
                    LexisNexis Risk Solutions Consumer Center, Atlanta.
                  </span>
                </p>
                <div className="table-list">
                  {openLetters
                    .filter((l) => l.kind === "lexisnexis_dispute")
                    .map((l) => (
                      <div className="table-row" key={l.id}>
                        <span>{l.id.slice(0, 8)}</span>
                        <strong>LexisNexis Risk Solutions</strong>
                        <span>{letterDisplayKind(l)}</span>
                        <StatusPill status={l.status} />
                        <button
                          className="ghost-button"
                          onClick={async () => {
                            try {
                              await downloadPdf(
                                `/api/letters/${l.id}/download`,
                                `lexisnexis-${l.id.slice(0, 8)}.pdf`,
                              );
                            } catch (err: any) {
                              setDetailErr(err?.message || "Download failed");
                            }
                          }}
                          title="Download the LexisNexis letter as a standalone PDF"
                        >
                          <Download size={14} aria-hidden /> Download PDF
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {(open as any).notes && (
              <div>
                <span className="field-label">Notes</span>
                <p style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{(open as any).notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Lob submission modal — staff confirms before any letter is mailed.
          CROA: never auto-mail. Confirm is mandatory; bureau and addresses
          shown so the operator can sanity-check before the bundle goes out. */}
      <Modal
        open={!!lobBureau && !!open}
        title={
          lobResult
            ? "Submitted to Lob"
            : `Mail via Lob — ${
                lobBureau === "experian"
                  ? "Experian"
                  : lobBureau === "transunion"
                  ? "TransUnion"
                  : lobBureau === "equifax"
                  ? "Equifax"
                  : ""
              } bundle`
        }
        onClose={() => {
          if (lobSubmitting) return;
          setLobBureau(null);
          setLobResult(null);
          setLobErr(null);
        }}
        footer={
          lobResult ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="primary-button"
                onClick={() => {
                  setLobBureau(null);
                  setLobResult(null);
                  setLobErr(null);
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                className="ghost-button"
                disabled={lobSubmitting}
                onClick={() => {
                  setLobBureau(null);
                  setLobErr(null);
                }}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                disabled={lobSubmitting}
                onClick={submitLobMail}
                title="This will submit the bundle to Lob for physical mailing"
              >
                {lobSubmitting ? "Submitting…" : "Confirm and submit to Lob"}
              </button>
            </div>
          )
        }
      >
        {open && lobBureau && !lobResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div className="identity-grid">
              <div className="field-pair">
                <span className="field-label">Bureau</span>
                <span>
                  {lobBureau === "experian"
                    ? "Experian"
                    : lobBureau === "transunion"
                    ? "TransUnion"
                    : "Equifax"}
                </span>
              </div>
              <div className="field-pair">
                <span className="field-label">Round</span>
                <span>{open.round_number}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Client</span>
                <span>{open.client_name || open.client_id.slice(0, 8)}</span>
              </div>
            </div>

            <div>
              <span className="field-label">Mail class</span>
              <div style={{ display: "flex", gap: 16, marginTop: 6 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="lob-mail-type"
                    value="usps_first_class"
                    checked={lobMailType === "usps_first_class"}
                    onChange={() => setLobMailType("usps_first_class")}
                  />
                  USPS First-Class
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name="lob-mail-type"
                    value="certified"
                    checked={lobMailType === "certified"}
                    onChange={() => setLobMailType("certified")}
                  />
                  Certified
                </label>
              </div>
            </div>

            <div>
              <span className="field-label">From (client mailing address)</span>
              <p className="muted small" style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                Pulled from the client record. If incomplete the server will
                return a 400 with the missing fields; update the client and
                try again.
              </p>
            </div>

            <div>
              <span className="field-label">To (bureau address)</span>
              <pre className="muted small" style={{ marginTop: 4 }}>
                {lobBureau === "experian"
                  ? "Experian\nP.O. Box 4500\nAllen, TX 75013"
                  : lobBureau === "transunion"
                  ? "TransUnion LLC Consumer Dispute Center\nP.O. Box 2000\nChester, PA 19016-2000"
                  : "Equifax Information Services LLC\nP.O. Box 740256\nAtlanta, GA 30374-0256"}
              </pre>
            </div>

            <p className="muted small">
              Physical mail is never sent automatically. Pressing
              <strong> Confirm and submit to Lob </strong>
              re-renders the bundle PDF and posts it to Lob.
            </p>

            {lobErr && <p className="error-text">{lobErr}</p>}
          </div>
        )}

        {lobResult && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p>Bundle submitted to Lob successfully.</p>
            <div className="identity-grid">
              <div className="field-pair">
                <span className="field-label">Lob ID</span>
                <span style={{ fontFamily: "monospace" }}>{lobResult.lob_id}</span>
              </div>
              <div className="field-pair">
                <span className="field-label">Mail job</span>
                <span style={{ fontFamily: "monospace" }}>
                  {lobResult.mail_job_id.slice(0, 8)}
                </span>
              </div>
              {lobResult.expected_delivery && (
                <div className="field-pair">
                  <span className="field-label">Expected delivery</span>
                  <span>{lobResult.expected_delivery}</span>
                </div>
              )}
              {lobResult.tracking_url && (
                <div className="field-pair">
                  <span className="field-label">Tracking</span>
                  <a
                    href={lobResult.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Lob letter <ExternalLink size={12} aria-hidden />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </Shell>
  );
}
